import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext.jsx';
import { fn, logWebEvent, webPushMaybeSupported } from '../firebase.js';
import {
  fetchQuestion,
  fetchTodaySetMerged,
  fetchTodaySetPublic,
  getCachedToday,
  getPersistedToday,
  setCachedToday,
} from '../data/dailySet.js';
import TodaySkeleton from '../components/TodaySkeleton.jsx';
import { submitDailySet, writeAnswer } from '../util/playContract.js';
import { shareQuestionLink } from '../data/questionShare.js';
import { takeText, predictorTeaseText } from '../util/shareStory.js';
import { fetchUserProfile } from '../data/profile.js';
import { playDayString } from '../util/etDate.js';
import { readHookAnswer, clearHookAnswer } from '../util/pendingHookAnswer.js';
import { unlockAudio, playSubmitBump, playSubmitFanfare } from '../util/sound.js';
import { getScreenCache } from '../util/screenCache.js';
import QuestionCard from '../components/QuestionCard.jsx';
import A2HSWalkthrough from '../components/A2HSWalkthrough.jsx';
import WebPushOptIn from '../components/WebPushOptIn.jsx';
import NextSixCountdown from '../components/NextSixCountdown.jsx';
import { usePwaInstallable, promptPwaInstall, hideInstallPrompts, isStandalone } from '../util/pwaInstall.js';
import { rememberSignInLineFor } from '../util/authProvider.js';
import { isIOS, isAndroid, isDesktop } from '../util/platform.js';
import { appCheckReadyOrTimeout, withReadTimeout } from '../util/readGuard.js';
import { appStoreUrl } from '../util/appStore.js';
import HowItWorksModal from '../components/HowItWorksModal.jsx';
import ChangeAnswerModal from '../components/ChangeAnswerModal.jsx';
import ContextModal from '../components/ContextModal.jsx';
import HeadlineImage from '../components/HeadlineImage.jsx';
import BonusBallot from '../components/BonusBallot.jsx';
import BackSections from '../components/BackSections.jsx';
import SectionsDropdown from '../components/SectionsDropdown.jsx';
import SectionPage from '../components/SectionPage.jsx';
import CategoryIcon from '../components/CategoryIcon.jsx';
import {
  fetchSectionsGate,
  fetchSectionForCategory,
  fetchAvailableSectionCategories,
} from '../data/sections.js';
import { buildFocusedBallot, ballotProgress } from '../util/focusedBallot.js';

// Today's six — collect-then-submit (mirrors the iOS ballot):
//   1. Tap YES/NO on each card. Picks are held in local state — NOTHING is
//      written yet, so you can change any answer freely. One card may also be
//      marked as the 2x double-down pick (mutually exclusive) — stamped into
//      the batch write at submit time.
//   2. After each tap, the view snap-scrolls to the next question.
//   3. A fixed Submit bar enables once all six are answered. Submitting writes
//      all six at once (one batch → the server's all-six gate awards +6).
//   4. After submit, the cards LOCK — YES/NO picks can't change directly, but
//      the 2x pick can still be MOVED (free, once/day) and an answer can be
//      CHANGED (30 coins, once/question/day) via the changeDoubleDown /
//      changeAnswer callables — both platform-agnostic, shipped 2026-07-08.
//
// No live consensus (iOS-only, plan §5).

// Mirror of the server submission-award math (`awardSubmissionPoints`,
// functions/src/index.ts) so the web can show what today's submission earned
// without diffing balances. Given the post-award streak:
//   coins = (daily-six questions answered) + (streak % 6 === 0 ? 6 : 0)
// FLAT cadence (Tom 2026-07-16): the day-number milestone overrides
// (30/60/90/… paying their day-count) are RETIRED — one rule everywhere.
// `bonus` is true on every 6-day cadence hit. Keep in lockstep with the server.
// (Points retired 2026-07-15 — the strip celebrates coins + streak only.)

// Per-uid local "already saw the bonus ballot" flag. A fast pre-check so the
// overlay doesn't re-open on a same-session re-render BEFORE the server gate
// read returns; the server gate (hasSeenFirstSubmissionBonusWeb) is the real
// one-shot guard, so a wiped localStorage just falls back to it (the ballot
// then self-closes via getWebBonusBallot's claimed:true).
const BONUS_SEEN_KEY = 'ps_bonus_seen'; // value: uid that has seen it
function bonusBallotSeenLocally(uid) {
  try {
    return localStorage.getItem(BONUS_SEEN_KEY) === uid;
  } catch {
    return false;
  }
}
function markBonusBallotSeenLocally(uid) {
  try {
    if (uid) localStorage.setItem(BONUS_SEEN_KEY, uid);
  } catch {
    /* storage unavailable — server gate still protects us */
  }
}

// `sixAnswered` = how many of TODAY'S SIX the player actually answered (skips
// and back-section answers excluded). Since 2026-07-28 the server pays one coin
// per answered daily-six question rather than a flat 6, because the streak gate
// is now "six answers, any lane" and sections pay nothing. Callers that don't
// know the count yet default to a full six.
function computeSubmissionEarned(streak, sixAnswered = 6) {
  const bonusHit = streak > 0 && streak % 6 === 0;
  const base = Math.max(0, Math.min(6, sixAnswered));
  return {
    streak,
    coins: base + (bonusHit ? 6 : 0),
    bonus: bonusHit,
  };
}

// The active head-to-head / group matchup the user accepted from a web invite
// (stamped by JoinConfirmation). Read once on Play mount to (1) show the
// "you're playing X" banner and (2) auto-fire the lock-in auth ask after the
// first submit. 24h TTL so a stale matchup can't resurface days later.
function readActiveMatchup() {
  try {
    const raw = localStorage.getItem('ps_active_matchup');
    if (!raw) return null;
    const m = JSON.parse(raw);
    if (!m?.at || Date.now() - m.at > 24 * 60 * 60 * 1000) {
      localStorage.removeItem('ps_active_matchup');
      return null;
    }
    return m;
  } catch {
    return null;
  }
}

export default function Play() {
  const { status, uid, claimUserDoc, isAnonymous, openSaveAccount, openLogin, providerId } = useSession();
  const navigate = useNavigate();
  // Seed from the in-memory cache so returning to Today (e.g. from Results)
  // is instant — no refetch, no "Loading…" flash, in-progress picks intact.
  // Seed from the warm in-memory cache OR the persisted (localStorage) cache so
  // BOTH a tab revisit and a full reload render instantly — no "Loading…" flash.
  const [questions, setQuestions] = useState(() => (getCachedToday(uid) || getPersistedToday(uid))?.questions ?? null);
  const [phase, setPhase] = useState(() => ((getCachedToday(uid) || getPersistedToday(uid)) ? 'ready' : 'loading')); // loading | ready | noset | error
  const [submitted, setSubmitted] = useState(() => (getCachedToday(uid) || getPersistedToday(uid))?.submitted ?? false);
  // THE BIG PICTURE (web parity W1) — today's headline URL. Seeded from the
  // same caches as the questions (they ride one payload), so a same-day
  // reopen paints the hero on the first frame instead of after the refetch.
  const [headlineURL, setHeadlineURL] = useState(
    () => (getCachedToday(uid) || getPersistedToday(uid))?.headlineImageURL ?? null
  );
  // The context modal's target (web parity W2) — a question ID + which lane
  // it lives in. Stored as a locator, not a snapshot, so the modal always
  // renders the LIVE question object (an answer made inside the modal must
  // show if it's reopened).
  const [contextTarget, setContextTarget] = useState(null);
  // The sections dropdown (web parity W4) — null = Daily Six, else a category
  // whose SectionPage replaces the ballot. `availableSections` is today's
  // categories with published sections (null while unresolved); the dropdown
  // renders only when the §3f gate is open AND at least one exists.
  const [activeSection, setActiveSection] = useState(null);
  const [availableSections, setAvailableSections] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showA2HS, setShowA2HS] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // Post-submit "change answer" confirm flow (changeAnswer callable, 30 coins
  // or free for a founder's first change of the ET day, server-enforced).
  // `changeAnswerIndex` is the question offered a flip; null = modal closed.
  const [changeAnswerIndex, setChangeAnswerIndex] = useState(null);
  const [changeAnswerBusy, setChangeAnswerBusy] = useState(false);
  const [changeAnswerError, setChangeAnswerError] = useState(null);
  const [sharedExpired, setSharedExpired] = useState(false);
  // The shared question is LIVE in a back section (not in the six) — its
  // category, for the honest banner. Mutually exclusive with sharedExpired:
  // "wrapped up" fired for ANY question outside today's six, which was often
  // false — a section share is live all day (storyboard finding E, AF-0).
  const [sharedLiveSection, setSharedLiveSection] = useState(null);
  // Post-submit "send a friend one of today's six" picker (the viral beat).
  // User chooses which question to share (mirrors the iOS SHARE A PREDICTION
  // flow); `copied` flashes a confirmation on the desktop clipboard fallback.
  const [sharePickerOpen, setSharePickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Coins earned from question-shares this session, accumulated +6 per newly
  // claimed question (up to 36/day). DURABLE — it stays on screen so the user
  // sees the reward when they return from the share sheet, rather than missing
  // a 2.8s flash that fired while they were still in Messages (the bug Tom hit:
  // the +6 credited server-side but was never visible). null=none yet.
  const [shareCoinsEarned, setShareCoinsEarned] = useState(0);
  // Set when a re-shared question was already claimed today, so the (correct)
  // server no-op doesn't read as "the bonus is broken."
  const [shareAlreadyClaimed, setShareAlreadyClaimed] = useState(false);
  // Question ids sent this session — drives the gold "already shared" gradient
  // on the picker row. Session-only (resets on reload); the server bucket is the
  // source of truth for the +6-per-question cap.
  const [sharedQids, setSharedQids] = useState(() => new Set());
  // Streak + coins earned for today's submission, surfaced in the
  // locked card (mirrors the iOS post-submit beat). Populated by the poll
  // effect below once the async award lands. null until then.
  const [earned, setEarned] = useState(null);
  // Set when the post-submit award poll exhausts without confirming (cold
  // function / throttled background tab) → show a static fallback instead of no
  // feedback (audit B13). A later full load re-runs the poll and upgrades it.
  const [earnedPending, setEarnedPending] = useState(false);
  // First-submission BONUS BALLOT (the founder twist — auth-gated 12-question
  // round that replaces iOS's group-invite Day-1 step). Opened once, after a
  // first-time player's submission award lands; the SERVER gate
  // (hasSeenFirstSubmissionBonusWeb) is the real one-shot guard, with a local
  // flag as a fast pre-check so we don't re-open on a same-session re-render.
  const [showBonusBallot, setShowBonusBallot] = useState(false);
  const bonusBallotTriggered = useRef(false);
  // R2 (Tom 2026-07-07) — the PRE-submit streak-at-risk line's data. A
  // separate read from the post-submit `earned` poll above: populated
  // best-effort on mount (cache-first) for a RETURNING, not-yet-submitted
  // user. Null until it resolves; never blocks render.
  const [preSubmitStreak, setPreSubmitStreak] = useState(null);
  // VP-2 (Tom 2026-07-16) — the sharer's Predictor handle, or null. An approved
  // Predictor's question shares invite to their LEAGUE instead of minting a
  // head-to-head; see `shareQuestion` below for why that matters. Resolved once
  // per uid, cache-first, best-effort.
  //
  // Null on a failed read means "share as a normal user", which would be the
  // wrong fork — so the SERVER refuses the dangerous half (createH2HInvite with
  // a hookQuestionId from an isCreator account, invites.ts) and this screen's
  // existing catch degrades that to a plain /q content share. A missed League
  // invite costs one follower; a wrong H2H costs the product's premise.
  const [predictorHandle, setPredictorHandle] = useState(null);
  const cardRefs = useRef([]);
  const submitRef = useRef(null);
  const lockedRef = useRef(null);
  const playLoadedLogged = useRef(false);
  const bonusAttempted = useRef(false);
  // C1 / R2 telemetry — each fires at most once per mount (guarded refs),
  // the moment its respective streak-led/streak-risk copy first renders.
  const streakCtaLoggedRef = useRef(false);
  const streakRiskLoggedRef = useRef(false);
  // Set true on a fresh in-session submit so the celebration FANFARE fires only
  // then (when the earned streak lands) — NOT when returning to an already-locked
  // Today, where the poll also runs but no new submission happened.
  const fanfarePendingRef = useRef(false);
  // Web-invite matchup context (JoinConfirmation → here). Read once: drives the
  // "you're playing X" banner + the one-time lock-in auth ask after submit.
  const [matchup] = useState(() => readActiveMatchup());

  // ── Back Sections (Front Page & Back Sections — web parity, 2026-07-28) ────
  // `sectionsVisible` is the §3f gate, answered by the server (web has no
  // Remote Config — see data/sections.js). Null while unknown, so nothing
  // section-shaped renders before we know the answer; it fails closed.
  const [sectionsVisible, setSectionsVisible] = useState(null);
  // The CATEGORY CHALLENGE arena this player arrived on, carried from the
  // invite by JoinConfirmation. Non-null ⇒ their friend scoped this game to one
  // category, so the ballot leads with it.
  const focusCategory = matchup?.focusCategory || null;
  // That category's three back-section questions, interleaved INTO the ballot
  // (not appended after it) — see util/focusedBallot.js for the order and why.
  const [focusSections, setFocusSections] = useState([]);
  // The user's saved interests, needed to decide whether the post-six flow
  // opens on the picker or straight into questions.
  const [sectionPrefs, setSectionPrefs] = useState(null);
  // Focused arrivals play in TWO beats. Beat one is the six-card arena ballot
  // Tom specified; beat two adds the daily-six questions it held back. The
  // second beat is not optional garnish — `awardSubmissionPoints` only fires
  // once ALL SIX ids in dailySets/{today} have votes, so stopping after beat
  // one would mean no coins, no streak, and no `lastSubmissionPointsDate`
  // while the optimistic celebration had already promised all three.
  const [focusStageTwo, setFocusStageTwo] = useState(false);
  // THE INTERMISSION (Tom, 2026-08-11): on a League first run, finishing the
  // first six lands on a moment — not silently on three more questions.
  const [focusIntermission, setFocusIntermission] = useState(false);
  const h2hAuthFiredRef = useRef(false);

  // After the first submit on an invite-origin session, auto-fire the "lock in
  // your matchup" auth ask once (anon only) — the post-submit beat is the
  // payoff, so we ask AFTER, never before. One-time across reloads
  // (localStorage), and the !isAnonymous guard stops it re-firing once saved.
  useEffect(() => {
    if (!submitted || !isAnonymous || !matchup || h2hAuthFiredRef.current) return undefined;
    try {
      if (localStorage.getItem('ps_h2h_auth_fired') === '1') {
        h2hAuthFiredRef.current = true;
        return undefined;
      }
    } catch {
      /* storage unavailable — fall through and fire once this session */
    }
    h2hAuthFiredRef.current = true;
    try { localStorage.setItem('ps_h2h_auth_fired', '1'); } catch { /* best effort */ }
    // Let the celebration LAND first (audit R25): the pips stagger to ~0.7s and
    // the fanfare peaks around 2.5s — the old 1.6s ask covered its own payoff
    // mid-beat. League-origin sessions get their own save reason (audit R26).
    const t = setTimeout(() => {
      logWebEvent('web_h2h_auth_autofired', { isH2H: !!matchup.isH2H, isLeague: !!matchup.isLeague });
      openSaveAccount({
        reason: matchup.isLeague ? 'predictor_follow' : 'h2h_lockin',
        inviter: matchup.inviter || '',
      });
    }, 3000);
    return () => clearTimeout(t);
  }, [submitted, isAnonymous, matchup, openSaveAccount]);

  // ── Predictor share hand-off (Tom 2026-08-11; was a popup gate 2026-07-17) ──
  // A Predictor's "Invite others to Play" share lands here as
  // /play?q=<id>&f=<handle> (the apex /q redirect carries ?f through). It used
  // to pop a follow POPUP over the ballot and ask before letting anyone play —
  // the last invite door that still did, once /i, /f and /challenge all became
  // answer-first. It now hands off to /f/<handle>, which IS that flow, and
  // carries ?q= so the shared question is the one they answer.
  //
  // What survives here is only the decision of WHETHER to hand off: an existing
  // follower, the Predictor themselves, or a bad handle all fall straight
  // through to the plain ballot, exactly as before.
  const followGateResolvedRef = useRef(false);
  // Withholds the Today ballot's paint while a ?f=<handle> resolution is in
  // flight, so the hand-off — not a flash of the ballot we're about to leave —
  // is what the visitor sees (Tom 2026-07-18). Initialized
  // synchronously from the URL/localStorage so there's no first-render gap
  // where this is still `false`. Released (see the effect below) the moment
  // we know either way: gate shown, or skipped/errored straight to plain play.
  const [followGatePending, setFollowGatePending] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('f');
    if (!handle) return false;
    // The return leg from /f/<handle> (see SaveHandoff): they've just followed,
    // so there is nothing to resolve and nothing to hand off — paint the ballot
    // immediately. Without this the hand-off round-trips forever-ish: Play
    // resolves, sees alreadyFollowing, and the new user stares at a withheld
    // ballot for the length of a callable on the very screen they came for.
    if (params.get('fs') === '1') return false;
    try {
      // Legacy: the retired follow POPUP wrote this on "not now". Nothing
      // writes it any more, but an old decline is still worth honouring.
      if (localStorage.getItem(`ps_follow_skip_${handle}`) === '1') return false;
    } catch { /* storage unavailable — still withhold; the effect resolves it */ }
    return true;
  });

  useEffect(() => {
    if (followGateResolvedRef.current || status !== 'ready' || !uid) return;
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('f');
    if (!handle || params.get('fs') === '1') return;
    followGateResolvedRef.current = true;
    try {
      if (localStorage.getItem(`ps_follow_skip_${handle}`) === '1') {
        setFollowGatePending(false);
        return;
      }
    } catch { /* storage unavailable — just resolve + show */ }
    let cancelled = false;
    (async () => {
      try {
        // A Predictor-share visit is a COLD first visit — this callable is
        // usually the session's first App-Check-protected call, exactly the
        // cold-mint rejection case (see web-appcheck-first-callable-warm /
        // FollowLanding). Await the startup pre-warm + retry through transient
        // failures; otherwise one cold-mint reject silently degrades the whole
        // follow-first funnel to plain play. Terminal not-found (bad handle)
        // still skips the gate immediately.
        // Bounded pre-warm + per-attempt timeout (audit R1) — a hung resolve
        // must degrade to plain play in seconds, not hold the withheld ballot.
        await appCheckReadyOrTimeout();
        let r;
        let lastErr;
        for (let i = 0; i < 3; i += 1) {
          try {
            r = await withReadTimeout(fn.resolveFollowLink({ handle }), 2500, 'follow gate');
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            const code = (e?.code || '').toLowerCase();
            if (code.includes('not-found') || code.includes('invalid-argument')) return; // bad handle → plain play
            if (i < 2) await new Promise((res) => setTimeout(res, 350 * (i + 1)));
          }
        }
        if (lastErr) throw lastErr;
        const creator = r?.data?.creator;
        if (cancelled || !creator || r.data.alreadyFollowing || creator.uid === uid) return;
        // THE FOURTH DOOR JOINS THE OTHER THREE (Tom, 2026-08-11). A
        // Predictor's "invite others" share landed here as ?q=<id>&f=<handle>
        // and popped a follow POPUP over the ballot — the one invite path that
        // still asked before it let anyone play, while /i and /f had both
        // become answer-first. Rather than rebuild that flow inside Play, hand
        // off to the door that already IS it. `?q=` rides along so the
        // question they were shown becomes the hook they answer.
        //
        // Every "don't gate" condition above still runs first, so an existing
        // follower or the Predictor themselves is never bounced — they fall
        // through to the plain ballot exactly as before.
        const sharedQ = new URLSearchParams(window.location.search).get('q');
        const target = `/f/${encodeURIComponent(handle)}${sharedQ ? `?q=${encodeURIComponent(sharedQ)}` : ''}`;
        logWebEvent('web_predictor_follow_gate_redirected', { handle, hadQuestion: !!sharedQ });
        navigate(target, { replace: true });
        return;
      } catch (e) {
        console.error('[Play] follow-gate resolve failed (gate skipped):', e);
        logWebEvent('web_predictor_follow_gate_error', { handle, code: e?.code || 'unknown' });
      } finally {
        // Release the withhold regardless of outcome — either the gate is
        // now showing (as an overlay on top of the ballot) or we've decided
        // not to show it, but either way the ballot is safe to paint now.
        if (!cancelled) setFollowGatePending(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status, uid]);

  // Platform split for the post-submit "get the app" CTA:
  //   Android → illustrated Add-to-Home-Screen walkthrough (no native app)
  //   iOS     → App Store (the native app)
  //   Desktop → install OUR PWA in-browser (the App Store is useless on a laptop)
  // Device platform — consolidated in util/platform.js so every install-funnel
  // gate (here, Profile, AppShell) shares one source of truth (iPadOS-on-Mac
  // heuristic included). iOS → App Store; Android → A2HS/PWA; Desktop → in-
  // browser PWA install.
  const onIOS = isIOS();
  const onAndroid = isAndroid();
  const onDesktop = isDesktop();
  const canInstallPwa = usePwaInstallable();
  // C1 (Tom 2026-07-07): once `earned` lands post-submit, a streak ≥2 makes
  // the install pitch LEAD with the streak (loss-aversion) instead of the
  // generic 12-bonus-picks pitch. `earned` is null pre-submit, so this is
  // false until the poll effect below resolves it.
  const streakLed = typeof earned?.streak === 'number' && earned.streak >= 2;

  const load = useCallback(
    async (force = false) => {
      if (status !== 'ready' || !uid) return;
      // A shared question (?q=<id>) floats that question to the top of today's
      // six; if it's no longer in today's set, the expired banner shows instead.
      const sharedQid = new URLSearchParams(window.location.search).get('q');
      setSharedExpired(false);
      setSharedLiveSection(null);
      // Cache hit → render instantly, skip the network round-trip (and flash).
      // `force` (retry) always re-fetches; a ?q land also re-fetches so the
      // reorder applies to a fresh set.
      let painted = false;
      if (!force && !sharedQid) {
        // Warm (same-session) hit → paint and skip the refetch entirely.
        const warm = getCachedToday(uid);
        if (warm) {
          setQuestions(warm.questions);
          setSubmitted(warm.submitted);
          setHeadlineURL(warm.headlineImageURL ?? null);
          setPhase('ready');
          return;
        }
        // Cold (persisted) hit → paint instantly (no loading wall), then fall
        // through to revalidate in the background.
        const cold = getPersistedToday(uid);
        if (cold) {
          setQuestions(cold.questions);
          setSubmitted(cold.submitted);
          setHeadlineURL(cold.headlineImageURL ?? null);
          setPhase('ready');
          painted = true;
        }
      }
      if (!painted) setPhase('loading');
      try {
        const { questions: merged, headlineImageURL } = await fetchTodaySetMerged(uid);
        if (!merged) {
          setPhase('noset');
          return;
        }
        // Already submitted earlier today? Every question must have a server
        // answer — OR be a resolved card (auto-satisfied, never written; mirrors
        // the submit + allAnswered gates so a set that contained a mid-day-
        // resolved question doesn't re-open as unlocked on reload) (audit B2).
        const allServerAnswered =
          merged.length > 0 &&
          merged.every(
            (q) =>
              typeof q.answer === 'boolean' ||
              q.isSkipped ||
              (q.isResolved && q.correctAnswer !== null)
          );
        // Float the shared question to the top, or flag it expired (not in set).
        let ordered = merged;
        if (sharedQid) {
          const idx = merged.findIndex((q) => q.id === sharedQid);
          if (idx > 0) {
            ordered = [merged[idx], ...merged.slice(0, idx), ...merged.slice(idx + 1)];
          } else if (idx === -1) {
            // Not in the six ≠ wrapped up (storyboard finding E): a section
            // share is live all day. Read the question itself before choosing
            // the banner — unresolved → point at its section (which unlocks
            // below once the six is in); resolved/absent → the honest
            // wrapped-up line. Non-blocking failure: a read error just keeps
            // the old copy, which is the pre-fix behavior.
            try {
              const shared = await fetchQuestion(sharedQid);
              if (shared && !shared.isResolved && !shared.isVoided) {
                setSharedLiveSection(shared.category);
              } else {
                setSharedExpired(true);
              }
            } catch {
              setSharedExpired(true);
            }
          }
        }
        // PRE-FILL FROM THE INVITE DOOR (Tom, 2026-08-11). The first question
        // they answered to accept an invite is no longer written on the tap —
        // it is stashed and lands here, so the ballot opens with that card
        // already answered and the single submit write covers it. Applied only
        // where the server has nothing yet: a real answer always wins.
        const stashed = readHookAnswer(playDayString());
        let stashLanded = false;
        const withStash = stashed
          ? ordered.map((q) => {
              if (q.id !== stashed.questionId || q.answer != null || q.isSkipped) return q;
              stashLanded = true;
              return { ...q, answer: stashed.answer };
            })
          : ordered;
        // A hook that ISN'T in today's six (the server picks hooks from the
        // arena/category, and a Predictor share can name any live question)
        // has no card to pre-fill, so it would evaporate on the walk above.
        // Write it on its own instead — the same single-question write the /q
        // door uses — rather than lose the first answer a new user ever gave.
        if (stashed && !stashLanded && stashed.question) {
          writeAnswer(uid, stashed.question, { answer: stashed.answer })
            .then(() => clearHookAnswer())
            .catch((e) => console.warn('[Play] off-six hook write failed:', e));
        }
        setQuestions(withStash);
        setSubmitted(allServerAnswered);
        setHeadlineURL(headlineImageURL ?? null);
        setCachedToday(uid, {
          questions: ordered,
          submitted: allServerAnswered,
          headlineImageURL: headlineImageURL ?? null,
        });
        setPhase('ready');
      } catch (e) {
        console.error('[Play] load failed:', e);
        // If we already painted from the persisted cache, keep it on a
        // background-refresh failure rather than dropping to the error state.
        setPhase((p) => (p === 'ready' ? 'ready' : 'error'));
      }
    },
    [status, uid]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Cold-start FAST PAINT: fetch today's six from the PUBLIC CDN endpoint (no
  // auth, no App Check, no SDK round-trip) so the ballot can appear before the
  // anonymous session + App Check mint + Firestore reads finish. Runs once on
  // mount; the authoritative load() above supersedes it with the user's merged
  // picks once the session is ready. We only paint when we have nothing yet (a
  // cache hit already short-circuits), and never clobber an authoritative set
  // that has already landed.
  useEffect(() => {
    if (questions) return; // cache hit / already painted → skip the public fetch
    let cancelled = false;
    (async () => {
      const pub = await fetchTodaySetPublic();
      if (cancelled || !pub?.questions?.length) return;
      const sharedQid = new URLSearchParams(window.location.search).get('q');
      let ordered = pub.questions;
      if (sharedQid) {
        const idx = ordered.findIndex((q) => q.id === sharedQid);
        if (idx > 0) {
          ordered = [ordered[idx], ...ordered.slice(0, idx), ...ordered.slice(idx + 1)];
        }
      }
      setQuestions((cur) => cur ?? ordered); // don't overwrite the merged set
      setHeadlineURL((cur) => cur ?? pub.headlineImageURL ?? null);
      setPhase((p) => (p === 'loading' ? 'ready' : p)); // dismiss the skeleton
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funnel entry (audit C3) + the +60 secure-account bonus self-heal (audit C1).
  // Once the session is ready: log the play-screen view once, and — for a
  // SECURED web user — re-attempt claimWebFirstSetBonus once. It no-ops
  // server-side unless eligible + unclaimed, so this rescues a save-before-play
  // user who saved on a day they hadn't yet completed a set (the claim in
  // SaveAccountModal would have returned "no-set-yet" then).
  useEffect(() => {
    if (status !== 'ready') return;
    if (!playLoadedLogged.current) {
      playLoadedLogged.current = true;
      logWebEvent('web_play_loaded', { anon: isAnonymous });
    }
    if (!isAnonymous && !bonusAttempted.current) {
      bonusAttempted.current = true;
      fn.claimWebFirstSetBonus().catch(() => {});
    }
  }, [status, isAnonymous]);

  // Back Sections: resolve the §3f gate once per session, then (only for a
  // category-challenged arrival) pull that arena's three questions so they can
  // be interleaved into the ballot. Sections are still dark for the public, so
  // for almost everyone this settles to `false` and nothing below changes.
  useEffect(() => {
    if (!uid) return;
    let alive = true;
    (async () => {
      const visible = await fetchSectionsGate();
      if (!alive) return;
      setSectionsVisible(visible);
      if (!visible) return;
      // The dropdown's menu (W4): which categories have a section today.
      // Best-effort — an empty list just means no dropdown renders.
      fetchAvailableSectionCategories()
        .then((cats) => alive && setAvailableSections(cats))
        .catch(() => alive && setAvailableSections([]));
      // Interests drive the post-six routing for UNFOCUSED players; a focused
      // arrival never sees the picker, so don't block on this.
      fetchUserProfile(uid)
        .then((p) => alive && setSectionPrefs(p?.sectionPreferences || []))
        .catch(() => alive && setSectionPrefs([]));
      if (focusCategory) {
        try {
          const qs = await fetchSectionForCategory(focusCategory, uid);
          if (alive) setFocusSections(qs);
        } catch {
          // Non-fatal: the ballot degrades to the plain six rather than
          // blocking the one thing this user came here to do.
          if (alive) setFocusSections([]);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [uid, focusCategory]);

  // Is this a live focused ballot? Only when the gate is open AND the arena
  // actually has questions today — otherwise `buildFocusedBallot` would still
  // reorder the six around a category for no reason.
  const focusActive =
    sectionsVisible === true && !!focusCategory && focusSections.length > 0;

  // The rendered order. Unfocused (the overwhelming majority today) → exactly
  // the six, in the order Play has always shown them.
  //
  // Focused → SIX cards: their category's question, that category's three
  // section questions, two others. The remaining daily-six questions come back
  // as `deferred` and are appended once the first ballot is in (below), because
  // the day's award needs votes on all six ids — see focusedBallot.js.
  const { entries: focusedEntries, deferred: deferredSix } = buildFocusedBallot(
    questions || [],
    focusActive ? focusSections : [],
    focusActive ? focusCategory : null
  );
  const ballot =
    focusStageTwo && deferredSix.length > 0
      ? [...focusedEntries, ...deferredSix]
      : focusedEntries;

  // A question that RESOLVED mid-day (has a correctAnswer) renders no Yes/No
  // buttons, so it can never be "answered" — treat those as auto-satisfied so
  // one resolving mid-day can't permanently block Submit (audit B2). A voided
  // card (correctAnswer === null) still renders buttons, so it stays answerable.
  // Counter + gate run over the still-answerable questions only.
  const isResolvedCard = (q) => q.isResolved && q.correctAnswer !== null;
  // Progress + the Submit gate. `requireSections` is Tom's 2026-07-28 call: a
  // focused arrival must answer their arena's three too, because those ARE the
  // week they were challenged to — an unfocused player is never held to them.
  const progress = ballotProgress(ballot, { requireSections: focusActive });
  const answeredCount = progress.answered;
  const allAnswered = !!questions && questions.length > 0 && progress.complete;
  const locked = submitted;
  // True while a focused arrival is still on beat one (the six arena cards).
  // Its Submit banks those and reveals the held-back daily-six questions
  // rather than running the celebration.
  const inStageOne = focusActive && !focusStageTwo && deferredSix.length > 0;

  // THE LEAGUE FIRST RUN (Tom, 2026-08-11) — an unsaved visitor who arrived
  // through a Predictor's League link and hasn't locked a six yet. Everything
  // gated on this protects the same thing: their first two minutes. No hero
  // image competing with the question, no "three more to go" after they think
  // they have finished, and an ending that asks for the install rather than
  // for another friend. Tom: "the entire focus for this process should be on
  // getting to the point at the end where they install the app after saving.
  // We don't care about them asking another friend to play until they've
  // actually invested a little bit more time into getting to know what we are
  // all about." It self-cancels the moment they save or submit once, so a
  // returning League follower gets the ordinary Today.
  const leagueArrival = !!matchup?.isLeague;
  // Pre-submit chrome (hero, dividers) — only while they are still filling it in.
  const leagueFirstRun = isAnonymous && leagueArrival && !locked;
  // The ENDING. Not gated on `isAnonymous`, because by the time they get here
  // they may have just saved — and "install after saving" is precisely the
  // moment Tom wants this funnel to end on.
  const leagueFirstFinish = leagueArrival;

  // Tap a pick — local only (no write). Snap-scroll to the next card.
  //
  // `kind` routes the write to the right piece of state: the six live in
  // `questions` (cached, shared with the rest of the screen), the focused
  // arena's three live in `focusSections`. `ballotIndex` is the position in the
  // RENDERED order, which is what the scroll should follow — on a focused
  // ballot that is not the same as the question's index within its own array.
  const handleAnswer = useCallback(
    (kind, index, answer, ballotIndex) => {
      if (locked || submitting) return;
      setSubmitError(null);

      let nextSix = questions;
      let nextSections = focusSections;
      if (kind === 'section') {
        nextSections = focusSections.map((x, i) =>
          i === index ? { ...x, answer, isSkipped: false } : x
        );
        setFocusSections(nextSections);
      } else {
        nextSix = questions.map((x, i) =>
          i === index ? { ...x, answer, isSkipped: false } : x
        );
        setQuestions(nextSix);
        // Keep the cache live so picks survive a tab switch mid-answering.
        // Section picks are deliberately NOT cached here: the Today cache is
        // the six's contract (getCachedToday/setCachedToday feed `questions`),
        // and widening it would change what every other reader of that cache
        // sees.
        setCachedToday(uid, { questions: nextSix, submitted });
      }

      // Does this tap complete the ballot? Recompute against the state we just
      // built rather than the stale closure values — composed EXACTLY like the
      // rendered `ballot` (entries + deferred in stage two). The first version
      // of this passed buildFocusedBallot's whole {entries, deferred} RESULT
      // into ballotProgress, which filters an array — every tap threw
      // "entries.filter is not a function" after the pick was set but before
      // the scroll, so picks registered and the snap-to-next-question silently
      // died (Tom caught it live, 2026-07-28).
      const { entries: nextEntries, deferred: nextDeferred } = buildFocusedBallot(
        nextSix,
        focusActive ? nextSections : [],
        focusActive ? focusCategory : null
      );
      const nextBallot =
        focusStageTwo && nextDeferred.length > 0
          ? [...nextEntries, ...nextDeferred]
          : nextEntries;
      const willComplete = ballotProgress(nextBallot, {
        requireSections: focusActive,
      }).complete;

      requestAnimationFrame(() => {
        // Glide to the Submit button once complete, otherwise to the next card.
        const target = willComplete
          ? submitRef.current
          : cardRefs.current[ballotIndex + 1] || submitRef.current;
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    [locked, submitting, questions, focusSections, focusActive, focusCategory, focusStageTwo, uid, submitted]
  );

  // 2x pick — mutually exclusive across the six (mirrors iOS
  // doublePickQuestionId). Pre-submit: local-only, stamped into the Firestore
  // batch at submit time via q.isDoubleDown (playContract.js submitDailySet).
  // Post-submit (locked): calls the free changeDoubleDown callable (once per
  // ET day, server-enforced) to MOVE the pick to a different question —
  // there's no "clear entirely" once submitted, so tapping the already-active
  // chip is a no-op.
  const handleToggleDoubleDown = useCallback(
    async (index) => {
      if (submitting) return;
      const target = questions[index];
      if (!target || target.isSkipped) return;

      if (!locked) {
        setSubmitError(null);
        const alreadyActive = target.isDoubleDown === true;
        const next = questions.map((x, i) => ({
          ...x,
          isDoubleDown: i === index ? !alreadyActive : false,
        }));
        setQuestions(next);
        setCachedToday(uid, { questions: next, submitted });
        return;
      }

      // Locked: move the pick post-submit. Resolved or already-the-pick →
      // nothing to do (server would reject/no-op both anyway).
      if (target.isResolved && target.correctAnswer !== null) return;
      if (target.isDoubleDown === true) return;
      try {
        setSubmitError(null);
        await fn.changeDoubleDown({ questionId: target.id });
        const next = questions.map((x) => ({ ...x, isDoubleDown: x.id === target.id }));
        setQuestions(next);
        setCachedToday(uid, { questions: next, submitted });
      } catch (e) {
        setSubmitError(e?.message || 'Could not move your 2× pick.');
      }
    },
    [locked, submitting, questions, uid, submitted]
  );

  // Open the change-answer confirm modal for a locked, unresolved card.
  const handleChangeAnswerRequest = useCallback((index) => {
    setChangeAnswerError(null);
    setChangeAnswerIndex(index);
  }, []);

  const closeChangeAnswerModal = useCallback(() => {
    if (changeAnswerBusy) return; // ignore backdrop/Esc while the call is in flight
    setChangeAnswerIndex(null);
    setChangeAnswerError(null);
  }, [changeAnswerBusy]);

  const confirmChangeAnswer = useCallback(async () => {
    if (changeAnswerIndex === null) return;
    const target = questions[changeAnswerIndex];
    if (!target) return;
    const newAnswer = !target.answer;
    setChangeAnswerBusy(true);
    setChangeAnswerError(null);
    try {
      await fn.changeAnswer({ questionId: target.id, newAnswer });
      const next = questions.map((x, i) =>
        i === changeAnswerIndex ? { ...x, answer: newAnswer } : x
      );
      setQuestions(next);
      setCachedToday(uid, { questions: next, submitted });
      setChangeAnswerIndex(null);
    } catch (e) {
      setChangeAnswerError(e?.message || 'Could not change your answer.');
    } finally {
      setChangeAnswerBusy(false);
    }
  }, [changeAnswerIndex, questions, uid, submitted]);

  // Beat one of a focused arrival: bank the six arena cards, then reveal the
  // daily-six questions the ballot held back. NO celebration here — the day's
  // award needs all six daily-six ids voted, and Play's celebration is
  // optimistic (it paints +6 and streak+1 the moment the write lands), so
  // firing it now would promise a reward that never arrives.
  const handleFocusStageOne = useCallback(async () => {
    if (!allAnswered || submitting || locked) return;
    unlockAudio();
    playSubmitBump();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await claimUserDoc('Guest'); // the user doc must exist before votes land
      // Durable immediately: a brand-new invitee who bounces here keeps every
      // pick they made. submitDailySet skips unanswered questions on its own,
      // so passing the whole six is safe — only the three they saw are written.
      await submitDailySet(uid, [...questions, ...focusSections]);
      // The stashed invite answer is durable now; drop it so a later visit
      // can't re-apply a stale pre-fill over server truth.
      clearHookAnswer();
      setCachedToday(uid, { questions, submitted: false });
      logWebEvent('web_focus_stage_one', { category: focusCategory });
      // A League arrival gets the moment; everyone else keeps the silent
      // advance into stage two (Tom, 2026-08-11: "i submitted the first 6
      // questions... then i was shown 3 more daily six questions below that
      // when I should have been shown the celebration and prompt to install,
      // finish the daily six can be a button below the celebration").
      if (leagueArrival) {
        setFocusIntermission(true);
        logWebEvent('web_focus_intermission_shown', { category: focusCategory });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setFocusStageTwo(true);
        requestAnimationFrame(() => {
          cardRefs.current[focusedEntries.length]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
    } catch (e) {
      console.error('[Play] focus stage one failed:', e);
      setSubmitError('Could not save those. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [
    allAnswered, submitting, locked, uid, questions, focusSections,
    focusCategory, focusedEntries.length, claimUserDoc, leagueArrival,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!allAnswered || submitting || locked) return;
    // Play the submit BUMP on the tap itself — within this user gesture so Web
    // Audio can unlock — mirroring iOS playSubmitBump(). The celebration fanfare
    // fires later, when the earned streak lands (see the poll below).
    unlockAudio();
    playSubmitBump();
    setSubmitting(true);
    setSubmitError(null);
    // Snap to the top immediately so the "Submitting Predictions" status (which
    // fills the celebration card's slot) is in view during the write — the user
    // sees progress, not a blank gap, before the locked-in card mounts.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      await claimUserDoc('Guest'); // user doc MUST exist before the votes land
      // One batch for the whole ballot, six-lane and section-lane together.
      // Since 2026-07-28 `awardSubmissionPoints` gates the streak on SIX
      // ANSWERS IN ANY LANE, so a focused ballot's three section answers do
      // count toward the day — but coins are still paid per answered DAILY-SIX
      // question only, and section answers pay zero at resolution too (FP-2
      // economy guard) while still stamping accuracy, exactly as from iOS.
      await submitDailySet(uid, focusActive ? [...questions, ...focusSections] : questions);
      clearHookAnswer();
      setSubmitted(true);
      // OPTIMISTIC CELEBRATION (Tom 2026-07-07): the earned strip + fanfare used
      // to wait on the async awardSubmissionPoints trigger (poll below) — landing
      // 3–8s after the tap, so the celebration felt disconnected from the action.
      // Everything the strip shows is knowable NOW: base coins are one per
      // ANSWERED daily-six question, and the new streak is the pre-submit streak + 1 for the
      // (overwhelmingly common) consecutive-day case. So paint the strip and play
      // the fanfare IMMEDIATELY on the write landing; the poll below still runs and
      // RECONCILES the exact streak + reveals the 6-day / +60 saved-account bonus
      // once the server award confirms (an `optimistic` flag keeps the poll gate
      // open). Base coins are exact; only the streak number can shift (rare
      // broken-streak case), and both bonus lines are additive — we never falsely
      // claim them. `optimistic` streaks that are a multiple of 6 still play the
      // bonus chiptune; the visual "6-day bonus" line waits for server confirmation.
      const cachedStreak = getScreenCache(`profile:${uid}`)?.currentStreak;
      const predictedStreak = Math.max(
        1,
        (typeof preSubmitStreak === 'number'
          ? preSubmitStreak
          : typeof cachedStreak === 'number'
            ? cachedStreak
            : 0) + 1
      );
      unlockAudio(); // ctx already unlocked by the bump; idempotent + defensive
      playSubmitFanfare(predictedStreak);
      fanfarePendingRef.current = false; // played optimistically — poll must not replay
      setEarned({
        streak: predictedStreak,
        // One coin per daily-six question actually ANSWERED — sections and
        // skips pay nothing, so a focused three-plus-three ballot honestly
        // paints "+3" instead of over-promising a six (2026-07-28). The
        // reconcile poll below re-reads the server's own count.
        coins: (questions ?? []).filter((q) => typeof q.answer === 'boolean').length,
        bonus: false, // the loud "6-day bonus" claim waits for server confirmation
        savedBonus: 0,
        optimistic: true, // keeps the reconcile poll below running
      });
      // Normalize to the server-merged shape (stamp `answered`) so a tab-switch
      // return reads the same per-question state a full reload would re-derive.
      setCachedToday(uid, {
        questions: questions.map((q) => ({
          ...q,
          answered: typeof q.answer === 'boolean' || q.isSkipped,
        })),
        submitted: true,
      });
      logWebEvent('web_set_submitted', { count: questions.length, anon: isAnonymous });
      // NOTE: the +60 secure-account bonus is NOT claimed here — at this instant
      // the async awardSubmissionPoints hasn't stamped lastSubmissionPointsDate
      // yet, so the claim would no-op ("no-set-yet"). The earned-summary poll
      // below claims it once it confirms the set landed (audit C1).
      // The locked card now renders ABOVE the six, and the [locked] effect snaps
      // the page to the top — here we just move screen-reader focus to the
      // confirmation. Double-rAF so the card has mounted + painted first (A3).
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          lockedRef.current?.focus?.(); // move SR focus to the confirmation (A3)
        })
      );
    } catch (e) {
      console.error('[Play] submit failed:', e);
      logWebEvent('web_set_submit_failed', {});
      setSubmitError('Could not submit your picks. Tap Submit to try again.');
    } finally {
      setSubmitting(false);
    }
  }, [allAnswered, submitting, locked, uid, questions, focusActive, focusSections, claimUserDoc, isAnonymous, preSubmitStreak]);

  // Once locked, surface what this submission earned (streak + coins +
  // 6-day bonus), mirroring the iOS post-submit beat. The award is an async
  // vote trigger (`awardSubmissionPoints`), so poll the user doc until it lands
  // — signalled by `lastSubmissionPointsDate === today` (stamped in the same
  // txn as the streak + coin increments). The earned amounts are a pure
  // function of the resulting streak (server formula: +1 coin per answered
  // daily-six question, +6
  // streak bonus every 6 days — flat), so we recompute them client-side
  // rather than diffing balances.
  useEffect(() => {
    // Runs until an AUTHORITATIVE earned lands. An OPTIMISTIC earned (painted on
    // submit for the instant celebration) does NOT stop the poll — it keeps
    // running to reconcile the exact streak + reveal the 6-day / saved-account
    // bonus once the server award confirms.
    if (!locked || !uid || (earned && !earned.optimistic)) return;
    let cancelled = false;
    // Must match the server's gate, which stamps lastSubmissionPointsDate with
    // playDayString() (3am-ET play-day) — NOT ET midnight. Comparing against ET
    // midnight here would never match between 12am–3am ET, so the earned summary
    // + secure-account bonus + ballot would silently never appear in that window.
    const today = playDayString();
    (async () => {
      for (let attempt = 0; attempt < 8 && !cancelled; attempt += 1) {
        try {
          const p = await fetchUserProfile(uid);
          // Restore today's share-bonus progress from the server bucket so the
          // durable +coins header AND the picker's gold "already shared"
          // gradients survive a reload (session state alone resets to 0).
          // Upward-only / union so a fresh in-session share is never clobbered.
          if (p?.questionShareBonusDay?.day === today && Array.isArray(p.questionShareBonusDay.qids)) {
            const qids = p.questionShareBonusDay.qids;
            setShareCoinsEarned((n) => Math.max(n, qids.length * 6));
            setSharedQids((prev) => {
              const next = new Set(prev);
              qids.forEach((id) => next.add(id));
              return next.size === prev.size ? prev : next;
            });
          }
          if (p && p.lastSubmissionPointsDate === today && p.currentStreak > 0) {
            // The streak number just landed — play the celebration fanfare (the
            // "streak counting up" beat on iOS). Only for a fresh in-session
            // submit, and the tune branches to the bonus chiptune on day 6/12/…
            if (!cancelled && fanfarePendingRef.current) {
              fanfarePendingRef.current = false;
              playSubmitFanfare(p.currentStreak);
            }
            // `submissionCoinsPaidFor` is the server's own count of the
            // daily-six questions it paid for today — the authoritative
            // version of the optimistic number painted at submit time.
            // Absent on records written before 2026-07-28 → fall back to 6.
            const base = computeSubmissionEarned(
              p.currentStreak,
              typeof p.submissionCoinsPaidFor === 'number' ? p.submissionCoinsPaidFor : 6
            );
            // Founder twist: once the submission award has landed, offer the
            // auth-gated first-submission bonus ballot — but only if the server
            // one-shot gate is still open and we haven't already triggered it
            // this session. The BonusBallot component itself self-closes if the
            // gate turns out to be claimed (e.g. on another device), so this is
            // a best-effort pre-check, not the authority.
            if (
              !cancelled &&
              // Installed/committed context ONLY — the 12-bonus is the reward for
              // installing (iOS app) or adding to the home screen (standalone PWA),
              // never a friction tax on the frictionless first six in a plain
              // browser tab. iOS-app users get the iOS bonus instead.
              isStandalone() &&
              !bonusBallotTriggered.current &&
              !p.hasSeenFirstSubmissionBonusWeb &&
              !p.hasSeenFirstSubmissionBonus && // already claimed in the iOS app → don't double-offer
              !bonusBallotSeenLocally(uid)
            ) {
              bonusBallotTriggered.current = true;
              setShowBonusBallot(true);
              logWebEvent('web_bonus_ballot_offered', { anon: isAnonymous, standalone: true });
            }
            // Now that the set has landed (lastSubmissionPointsDate is stamped),
            // a SECURED web user is eligible for the +60 secure-account bonus —
            // claim it here (idempotent) and fold the awarded coins into the
            // earned strip so a save-before-play user actually sees it (audit C1).
            let savedBonus = 0;
            if (!isAnonymous) {
              try {
                const r = await fn.claimWebFirstSetBonus();
                if (r?.data?.awarded) savedBonus = r.data.amount || 60;
              } catch {
                /* best-effort — the account is saved regardless */
              }
            }
            if (!cancelled) setEarned({ ...base, savedBonus });
            return;
          }
        } catch {
          /* transient read error — retry on the next tick */
        }
        await new Promise((r) => setTimeout(r, 900));
      }
      // Poll exhausted (cold function / a backgrounded tab throttled our timers)
      // — show a static "+6 earned, streak updating" fallback rather than no
      // feedback at all, and log it. A later full load re-runs this poll (earned
      // is component state, null on a fresh mount) and upgrades to real numbers
      // (audit B13).
      if (!cancelled) {
        setEarnedPending(true);
        logWebEvent('web_earned_poll_timeout', {});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locked, uid, earned, isAnonymous]);

  // R2 (Tom 2026-07-07) — the streak-at-risk line's PRE-submit profile read.
  // Deliberately separate from the post-submit `earned` poll above (which
  // only starts once locked): a RETURNING, not-yet-submitted user needs the
  // streak BEFORE they submit. Cache-first (the Profile tab's `profile:{uid}`
  // screen cache); only falls back to a Firestore read when that's cold.
  // Best-effort — any failure just leaves the line unrendered.
  useEffect(() => {
    if (!uid || locked) return undefined;
    let cancelled = false;
    const cached = getScreenCache(`profile:${uid}`);
    if (cached && typeof cached.currentStreak === 'number') {
      setPreSubmitStreak(cached.currentStreak);
      return undefined;
    }
    fetchUserProfile(uid)
      .then((p) => {
        if (!cancelled && p && typeof p.currentStreak === 'number') {
          setPreSubmitStreak(p.currentStreak);
        }
      })
      .catch(() => {
        /* best-effort — the line just doesn't show */
      });
    return () => {
      cancelled = true;
    };
  }, [uid, locked]);

  // VP-2 — resolve the Predictor handle (see the state declaration). Its own
  // effect, not folded into the streak read above: that one is gated on
  // `!locked` and returns early on a cache hit, and the share fork must be
  // resolved whether or not the ballot is locked.
  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    const cached = getScreenCache(`profile:${uid}`);
    if (cached?.isCreator && cached.creatorHandle) {
      setPredictorHandle(cached.creatorHandle);
      return undefined;
    }
    fetchUserProfile(uid)
      .then((p) => {
        if (!cancelled && p?.isCreator && p.creatorHandle) setPredictorHandle(p.creatorHandle);
      })
      .catch(() => {
        /* best-effort — the server guard is the backstop */
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // C1 telemetry — fires once (per mount, guarded by the ref) the instant the
  // streak-led install pitch is about to render (same gates as the pitch
  // itself: locked + not hideInstallPrompts + streakLed).
  useEffect(() => {
    // Gates mirror the render exactly (audit R30): the streak-led line shows
    // only on the SECURED locked card, so the event must too — this event
    // spent weeks counting impressions of a line that never rendered.
    if (streakCtaLoggedRef.current || !locked || !streakLed || isAnonymous || hideInstallPrompts()) return;
    streakCtaLoggedRef.current = true;
    logWebEvent('web_streak_cta_shown', {
      surface: 'play_locked',
      platform: onAndroid ? 'android' : onIOS ? 'ios' : 'desktop',
    });
  }, [locked, streakLed, isAnonymous, onAndroid, onIOS]);

  // R2 telemetry — fires once (per mount) the instant the pre-submit
  // streak-at-risk line is about to render.
  useEffect(() => {
    if (streakRiskLoggedRef.current || locked || typeof preSubmitStreak !== 'number' || preSubmitStreak < 2) {
      return;
    }
    streakRiskLoggedRef.current = true;
    logWebEvent('web_streak_risk_shown', { streak: preSubmitStreak });
  }, [locked, preSubmitStreak]);

  // (The post-submit rivalry-line fetch that used to live here was deleted in
  // the funnel-audit R30 cleanup: item #8 replaced the locked card's dynamic
  // lines with static honest copy in July, but the Firestore groups fetch +
  // 4s retry kept running for a line that no longer rendered.)

  // Once locked — whether from this session's submit OR from returning to Today
  // already submitted — snap the page to the very top so the "All six locked in"
  // card (now rendered above the six) is the first thing in view. Smooth scroll;
  // effectively a no-op when already at the top (e.g. a cold page load).
  useEffect(() => {
    if (locked) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [locked]);

  // Share a single question → the apex /q/<id> door (crawlable OG, bounces to
  // the play SPA). ?ref=<uid> drains into referral attribution on the friend's
  // signup. navigator.share on mobile; clipboard fallback elsewhere.
  const shareQuestion = useCallback(
    async (q, source = 'card') => {
      // WHO is sharing decides what the link does — not which button they
      // pressed (VP-2, Tom 2026-07-16).
      //
      // An approved PREDICTOR's question share invites to their League: their
      // audience plays them without minting a head-to-head with every fan,
      // which is the whole reason the League exists. Sharing from here used to
      // call createH2HInvite for them too — and that invite is MULTI-USE with
      // groupId:null, so the group mints per acceptance: one share to a
      // following would have handed every tapper their own private matchup
      // with the Predictor. Nobody hit it only because no Predictor had yet
      // shared to a real audience. A Predictor who genuinely wants one H2H
      // still starts it from Games; that's the deliberate act, this is the
      // habitual one.
      //
      // "Play with a Friend" (post_submit) for everyone else: mint a pending
      // H2H game with this question as the hook, then share the /q image link
      // carrying it (?h2h=<id>) — tapping it creates a "[you]'s Game". Other
      // sources (per-card) stay plain /q content shares. The +6-per-share
      // economy was retired 2026-07-01, so no coin reward either way; the
      // sharer earns the +180 only if a friend plays.
      const asPredictor = !!predictorHandle;
      let h2hInviteId = null;
      if (source === 'post_submit' && !asPredictor) {
        try {
          const res = await fn.createH2HInvite({ hookQuestionId: q.id });
          h2hInviteId = res?.data?.inviteId || null;
        } catch {
          /* fall back to a plain /q share if the invite mint fails */
        }
      }
      // The Take (Epic 1, 2026-07-02): the post-submit share leads with the
      // fact that a call has been MADE, not with the call itself — the
      // answer-first accept's post-commit reveal is the payoff, so the message
      // must not spoil it (stance concealed 2026-07-06; the share-artifact
      // spec's original "always reveal" was reversed then, and this comment
      // described the reverted copy until VP-2). The take closes with "Play
      // with me." (Tom 2026-07-05); the 6-coin challenge still fires silently
      // server-side when the link carries the ?h2h= invite — so a failed
      // invite mint falls back to plain text (iOS bails on mint failure; web
      // degrades to a content share).
      //
      // A Predictor gets the same concealment with a League tail
      // (predictorTeaseText) — and gets it from EVERY source, not just
      // post_submit: every question they share is a front door to their
      // League. Per-card shares by everyone else stay plain question text.
      const shareText = asPredictor
        ? predictorTeaseText(q.text)
        : source === 'post_submit' && typeof q.answer === 'boolean' && h2hInviteId
          ? takeText(q.text, q.answer)
          : q.text;
      const r = await shareQuestionLink({
        questionId: q.id,
        questionText: shareText,
        uid,
        source,
        h2hInviteId,
        followHandle: predictorHandle,
      });
      if (!r.shared) return;
      if (r.usedClipboard) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
      // Mark the question sent → its picker row gets the gold "already shared" gradient.
      setSharedQids((prev) => (prev.has(q.id) ? prev : new Set(prev).add(q.id)));
      if (source === 'card') {
        lockedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    // `predictorHandle` is load-bearing, not incidental: it resolves async and
    // decides the fork. Omitting it would close over the initial null forever,
    // and every Predictor share would silently take the H2H path — the exact
    // bug VP-2 exists to kill.
    [uid, predictorHandle]
  );

  if (status === 'starting' || phase === 'loading' || followGatePending) {
    // Six shimmer cards instead of a centered spinner — reads as content
    // arriving. With the public /api/today fast-paint, the real cards usually
    // replace this within a beat. Also covers the `?f=` follow-gate
    // resolution window so the popup is the first real content the visitor
    // sees, not the ballot flashing underneath it first.
    return <TodaySkeleton />;
  }

  if (phase === 'error') {
    return (
      <main className="ps-page ps-center">
        <p className="ps-muted">Couldn’t load today’s set.</p>
        <button onClick={() => load(true)} style={retryBtn}>
          Try again
        </button>
      </main>
    );
  }

  if (phase === 'noset') {
    return (
      <main className="ps-page ps-center">
        <div className="ps-card">
          <p style={{ fontWeight: 800, fontSize: 18, marginTop: 0 }}>
            Today’s six aren’t live yet
          </p>
          {/* No time-of-day claim (Tom 2026-07-07): the play day rolls at 3 AM
              ET (rule 12 — "midnight ET" was wrong), and this state usually
              means today's set just hasn't published yet — a clock would
              mislead. */}
          <p className="ps-muted" style={{ margin: 0, lineHeight: 1.5 }}>
            A fresh set of six drops every day. Check back soon.
          </p>
        </div>
      </main>
    );
  }

  // A SECTION PAGE replaces the ballot (web parity W4) — the dropdown stays
  // on top as the way back, exactly like the iOS title dropdown. `key` forces
  // a clean remount (fresh fetch) per category switch. The ballot's state is
  // all preserved underneath — switching back re-renders it untouched.
  //
  // The category's DAILY-SIX question rides along, pinned atop the section
  // (Tom 2026-07-30) — locked once the six is submitted; before that, a pick
  // made on the section page flows back into the ballot's state + cache and
  // still submits from Today (one write path for the six, always).
  if (activeSection) {
    const sixQ = questions?.find((q) => q.category === activeSection) || null;
    return (
      <main className="ps-page">
        <SectionsDropdown
          active={activeSection}
          categories={availableSections || []}
          onSelect={setActiveSection}
        />
        <SectionPage
          key={activeSection}
          category={activeSection}
          uid={uid}
          sixQuestion={sixQ}
          sixLocked={locked || submitting}
          onAnswerSix={
            sixQ && !locked && !submitting
              ? (ans) => {
                  const idx = questions.findIndex((q) => q.id === sixQ.id);
                  if (idx < 0) return;
                  const nextSix = questions.map((x, i) =>
                    i === idx ? { ...x, answer: ans, isSkipped: false } : x
                  );
                  setQuestions(nextSix);
                  setCachedToday(uid, { questions: nextSix, submitted });
                }
              : undefined
          }
        />
      </main>
    );
  }

  return (
    <main className="ps-page">
      {/* First-submission bonus ballot (founder twist) — a full-screen overlay
          that opens once after a first-time player's submission award lands.
          The auth gate + ballot + reward all live inside the component; the
          server one-shot gate prevents any re-award. On close we stamp the local
          seen flag so the same session never re-opens it. */}
      {showBonusBallot && (
        <BonusBallot
          onClose={() => {
            markBonusBallotSeenLocally(uid);
            setShowBonusBallot(false);
          }}
        />
      )}
      {/* The sections dropdown (web parity W4) — navigation chrome, so it
          leads the page. Renders only when the §3f gate is open and a
          category actually has a section today; the public sees nothing. */}
      {sectionsVisible === true && (availableSections?.length ?? 0) > 0 && (
        <SectionsDropdown
          active={null}
          categories={availableSections}
          onSelect={setActiveSection}
        />
      )}
      {/* Matchup banner — keeps the "you're playing X" context through the
          ballot when they arrived from a web invite (the celebration
          interstitial was dropped in favor of going straight to today's six). */}
      {matchup && (
        <div
          style={{
            margin: '0 0 14px',
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm)',
            background: 'color-mix(in srgb, var(--gold) 12%, transparent)',
            border: '1px solid var(--gold)',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          <p style={{ margin: 0, fontWeight: 800, fontSize: 14, color: 'var(--primary-text)' }}>
            {matchup.isLeague
              ? `You’re in ${matchup.inviter ? `@${String(matchup.inviter).replace(/^@+/, '')}` : 'your Predictor'}’s League`
              : matchup.isH2H
                ? matchup.focusCategory
                  ? `You’re playing ${matchup.inviter || 'a friend'} — ${matchup.focusCategory}, all week`
                  : `You’re playing ${matchup.inviter || 'a friend'}`
                : `You joined ${matchup.groupName || 'the group'}`}
          </p>
          {/* The arena rides the banner (AF-5, storyboard finding C's last
              crack): a Sports challenge said the generic six line here and
              the promise leaked. The generic lines stay for Classic. */}
          <p className="ps-muted" style={{ margin: '2px 0 0', fontSize: 12.5 }}>
            {matchup.isLeague
              ? matchup.focusCategory
                ? `A ${matchup.focusCategory} League — most correct calls Sunday to Saturday takes the week.`
                : 'Most correct calls Sunday to Saturday takes the week.'
              : matchup.isH2H
                ? matchup.focusCategory
                  ? `Your ${matchup.focusCategory} calls decide the week — start with today’s.`
                  : 'Predict today’s six — see who’s sharper.'
                : 'Predict today’s six to climb the board.'}
          </p>
          {/* HOW IT WORKS LIVES IN THE BOX (Tom, 2026-08-11). It used to float
              alone between the header and the ballot, which made it read as
              page chrome next to the account CTAs it sat with. Inside the
              matchup card it belongs to the sentence above it — "here is the
              game you're in, and here is what that means" — and it costs the
              ballot no vertical space of its own. Still anonymous-only: a
              saved player knows the ropes. */}
          {!locked && isAnonymous && (
            <button
              onClick={() => setShowHowItWorks(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                padding: 0,
                border: 'none',
                background: 'transparent',
                color: 'var(--gold)',
                fontWeight: 700,
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  width: 14,
                  height: 14,
                  borderRadius: 7,
                  border: '1.5px solid var(--gold)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9.5,
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                ?
              </span>
              How it works
            </button>
          )}
        </div>
      )}
      {/* NO ACCOUNT CHROME ABOVE THE BALLOT (Tom, 2026-08-11: "for new users,
          even if they didn't save, there should not be the Create account
          button or Log in link at the top of the page"). A first-time player
          arrives to play, not to sign up; the save ask has its own, better
          moment in the locked card after they submit. Removing the pair also
          removes the audit-C2 trap wholesale — a brand-new player can no
          longer tap "Log in", land on an empty account and orphan the picks
          they were mid-way through. Returning players still reach both from
          Profile. */}
      {/* THE BIG PICTURE IS GONE FROM WEB PLAY (Tom, 2026-08-11: "remove the
          Big Picture headline image from all of web play"). It was web parity
          with the iOS hero; on this surface it is the biggest thing on screen
          and it is never the thing to do. The League first-run suppression it
          replaces is now moot — nobody gets it. iOS keeps its own. */}

      {/* Streak-at-risk line (R2, Tom 2026-07-07) — a RETURNING user with a
          streak ≥2 who hasn't submitted yet sees a loss-aversion nudge above
          the ballot. Disappears the instant they submit/lock. */}
      {!locked && typeof preSubmitStreak === 'number' && preSubmitStreak >= 2 && (
        <StreakRiskLine streak={preSubmitStreak} />
      )}

      {/* Progress hint while answering — hidden once locked (the celebration
          card below becomes the header). Stays visible while submitting; the
          loading status sits directly beneath it. */}
      {!locked && (
        <div style={{ margin: '4px 0 16px' }}>
          <p className="ps-muted" style={{ margin: 0, textAlign: 'center' }}>
            {`${answeredCount} of ${progress.total} answered — tap YES or NO, then Submit`}
          </p>
        </div>
      )}

      {/* Post-submit loading status — sits directly below the "x of 6 answered"
          hint and above the first question card while the submit write is in
          flight (we scroll to the top the instant Submit is tapped so it's in
          view), so the user gets immediate feedback instead of a blank gap
          before the "locked in" card mounts. */}
      {submitting && !locked && (
        <div
          className="ps-card"
          role="status"
          aria-live="polite"
          style={{
            marginTop: 4,
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 14,
            padding: '34px 16px',
            textAlign: 'center',
          }}
        >
          <span className="ps-spinner" aria-hidden="true" />
          <p style={{ margin: 0, fontWeight: 900, fontSize: 18 }}>Submitting Predictions…</p>
          <p className="ps-muted" style={{ margin: 0, fontSize: 13.5 }}>Locking in your six</p>
        </div>
      )}

      {/* The "All six locked in" celebration card. Once submitted it renders
          ABOVE the six (the user's request) — both as the post-submit beat and
          as the first thing a returning, already-submitted player sees. The six
          question cards + Submit render BELOW it (see after this block). */}
      {locked && (
        <div
          ref={lockedRef}
          className="ps-card"
          tabIndex={-1}
          style={{ marginTop: 4, marginBottom: 20, scrollMarginTop: 12, outline: 'none' }}
        >
          {/* Celebratory confirmation — a popping gold check, six filling pips,
              and the reveal time. Animations are CSS (theme.css) and respect
              prefers-reduced-motion. Mirrors the app's "you're locked in" beat.
              role="status" announces the confirmation to screen readers (A3). */}
          <div role="status" style={{ textAlign: 'center', margin: '0 0 20px' }}>
            <div className="ps-celebrate-badge" aria-hidden="true">✓</div>
            <p className="ps-celebrate-title" style={{ fontWeight: 900, fontSize: 22, margin: '14px 0 4px' }}>
              All six locked in!
            </p>
            {/* Item #8 (Tom 2026-07-18): two static, honest lines — no clock
                promise (the play day + resolution timing vary), no rivalry
                spoiler. */}
            <p
              className="ps-celebrate-title"
              style={{ margin: 0, fontSize: 15, lineHeight: 1.45, color: 'var(--gold)', fontWeight: 800 }}
            >
              Predictions resolve as events unfold
            </p>
            <p className="ps-muted" style={{ margin: '4px 0 0', fontSize: 13.5, lineHeight: 1.4 }}>
              Check back daily to see how you did
            </p>
            <div className="ps-celebrate-pips" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} style={{ animationDelay: `${0.25 + i * 0.08}s` }} />
              ))}
            </div>

          </div>

          {/* Save your account (Tom 2026-07-20) — the primary funnel step for an
              anonymous player once they've submitted: a prominent gold button
              below the six pips and above Install. Replaces the top-of-page
              "Create account" pill (now hidden once locked) so there's one clear
              save CTA in the card flow. Generic save reason → the +60 self-heals
              after today's set is scored. */}
          {isAnonymous && (
            <button
              onClick={() => {
                logWebEvent('web_save_cta_tapped', { source: 'locked_card' });
                openSaveAccount();
              }}
              style={saveAccountBtn}
            >
              Save your account
            </button>
          )}

          {/* Install CTA — SECURED users only (audit R24): for an anonymous
              player, Save is the single gold primary above, and the install
              beat rides the save-success chain (InstallChainStep, audit R23) —
              the old anon Install button opened the very same save modal, so
              two identical gold CTAs competed at the peak moment. iOS → App
              Store; Android/desktop → native install prompt when offered, else
              the illustrated Add-to-Home-Screen walkthrough. */}
          {!isAnonymous && !hideInstallPrompts() && (
            <div style={{ margin: '0 0 14px' }}>
              {/* C1 (Tom 2026-07-07) — the loss-aversion lead line: with a
                  streak ≥2, the install pitch opens with what's at stake.
                  (Designed in July, but never actually rendered until the
                  audit-R30 fix — the telemetry below counted a ghost.) */}
              {streakLed && <StreakLeadLine streak={earned.streak} />}
              {onIOS ? (
                <a
                  href={appStoreUrl('web_locked_card')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => logWebEvent('web_install_cta_tapped', { platform: 'ios', anon: false })}
                  style={installBtnGold}
                >
                  Install the Predict Six App
                </a>
              ) : canInstallPwa ? (
                <button
                  onClick={async () => {
                    const platform = onAndroid ? 'android' : 'desktop';
                    logWebEvent('web_install_cta_tapped', { platform, native: true });
                    const outcome = await promptPwaInstall();
                    logWebEvent('web_install_prompt_outcome', { platform, outcome });
                  }}
                  style={{ ...installBtnGold, border: 'none', cursor: 'pointer' }}
                >
                  Install the Predict Six App
                </button>
              ) : (
                <button
                  onClick={() => {
                    logWebEvent('web_install_cta_tapped', { platform: onAndroid ? 'android' : 'desktop', native: false });
                    setShowA2HS((v) => !v);
                  }}
                  style={{ ...installBtnGold, border: 'none', cursor: 'pointer' }}
                >
                  Install the Predict Six App
                </button>
              )}
              {!onIOS && showA2HS && <A2HSWalkthrough />}
            </div>
          )}

          {/* Result alerts — un-nested from the install block (audit R12):
              dismissing the install pitch must not also kill the web-push
              opt-in; they're different asks. Secured users on a push-capable
              browser, any platform except iOS Safari tabs (no web push). */}
          {!isAnonymous && !onIOS && webPushMaybeSupported() && (
            <div style={{ margin: '0 0 14px' }}>
              <WebPushOptIn />
            </div>
          )}

          {/* Cross-over reminder for saved users (item #8): name the provider
              so signing into the app lands on the SAME account, not a new one.
              Null for anon/unknown → renders nothing. */}
          {!isAnonymous && rememberSignInLineFor(providerId) && (
            <p
              className="ps-center"
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--link-gold)',
                margin: '0 0 14px',
                lineHeight: 1.4,
              }}
            >
              {rememberSignInLineFor(providerId)}
            </p>
          )}

          {/* What this submission earned (streak · coins + 6-day bonus).
              Moved below the install/remember block (item #10); the
              saved-account-bonus line was dropped and the coins value carries
              a space ("+ 6"). */}
          {earned && (
            <div className="ps-earned" style={earnedWrap}>
              <div style={earnedStats}>
                <div style={earnedStat}>
                  <span style={{ ...earnedValue, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <FlameIcon /> {earned.streak}
                  </span>
                  <span style={earnedLabel}>day streak</span>
                </div>
                <div style={earnedDivider} aria-hidden="true" />
                <div style={earnedStat}>
                  <span style={earnedValue}>+ {earned.coins}</span>
                  <span style={earnedLabel}>{earned.coins === 1 ? 'coin' : 'coins'}</span>
                </div>
              </div>
              {earned.bonus && (
                <p style={earnedBonus}>
                  🎉 6-day streak bonus — extra coins!
                </p>
              )}
              {/* Close the "+60 banked" loop the save sheet promises (audit
                  R8) — the amount was fetched and folded into `earned` but
                  never shown. */}
              {earned.savedBonus > 0 && (
                <p style={earnedBonus}>
                  ✓ +{earned.savedBonus} coins banked for saving your account
                </p>
              )}
            </div>
          )}
          {!earned && earnedPending && (
            <div className="ps-earned" style={earnedWrap}>
              <p
                style={{
                  margin: 0,
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--gold)',
                  lineHeight: 1.4,
                }}
              >
                Coins banked · your streak is still updating — check back in a moment.
              </p>
            </div>
          )}

          {/* Bring a friend — the post-submit viral beat. Shares today's six via
              the apex /q/<id> door (floats one question to the top), so the
              friend predicts in seconds with no app or account; ?ref=<uid>
              drains into referral attribution. The per-card share icons still
              exist; this is the prominent prompt.

              WITHHELD ON THE LEAGUE FIRST FINISH (Tom, 2026-08-11). Someone who
              has played exactly one six, minutes after meeting the app through
              somebody else's link, has nothing to vouch for yet — and this block
              competes with the install CTA above it at the one moment that funnel
              exists to reach. His call: "we don't care about them asking another
              friend to play until they've actually invested a little bit more
              time into getting to know what we are all about." It returns on
              their next visit, when `ps_active_matchup` has expired or they are
              no longer arriving cold. The per-card share icons stay, so nothing
              is actually removed — only the prominent ask. */}
          {questions.length > 0 && !leagueFirstFinish && (
            <div style={shareInvite}>
              <p style={shareInviteTitle}>Play with a friend</p>
              <p className="ps-muted" style={shareInviteSub}>
                Send a friend one of today’s questions — when they play, you start a game together. You both get +180 coins.
              </p>
              {!sharePickerOpen ? (
                <button
                  onClick={() => {
                    const openPicker = () => {
                      setSharePickerOpen(true);
                      logWebEvent('web_share_picker_opened', { source: 'post_submit' });
                    };
                    // Save-gate (Tom 2026-07-20): an anon user's friend game is
                    // tied to this browser session — nudge them to save first so
                    // the game (and the invite) follow them into the app. Save →
                    // continue to the picker; a skip link keeps the viral path
                    // open for anyone who won't save.
                    if (isAnonymous) {
                      logWebEvent('web_save_cta_tapped', { source: 'friend_gate' });
                      openSaveAccount({
                        reason: 'friend_gate',
                        onContinue: openPicker,
                        continueLabel: 'Continue to your invite',
                        skipLabel: 'Continue without saving',
                      });
                    } else {
                      openPicker();
                    }
                  }}
                  style={shareInviteBtn}
                >
                  <PaperplaneIcon /> Play with a Friend
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <p
                    style={{
                      margin: '0 0 2px',
                      fontSize: 12.5,
                      fontWeight: 800,
                      color: 'var(--gold)',
                      textAlign: 'left',
                    }}
                  >
                    Pick a question to send
                  </p>
                  {questions.map((q) => {
                    const alreadyShared = sharedQids.has(q.id);
                    return (
                      <button
                        key={q.id}
                        // Keep the picker OPEN after a send so the row's gold
                        // "already shared" gradient is visible and the user can
                        // send more (each new question = +6, up to 36/day).
                        onClick={() => shareQuestion(q, 'post_submit')}
                        style={
                          alreadyShared
                            ? { ...sharePickRow, backgroundImage: SHARED_ROW_GRADIENT, borderColor: 'rgba(201,151,58,0.55)' }
                            : sharePickRow
                        }
                        aria-label={alreadyShared ? `${q.text} — already shared` : q.text}
                      >
                        <span style={sharePickText}>{q.text}</span>
                        {typeof q.answer === 'boolean' && (
                          <span style={sharePickPick}>{q.answer ? 'YES' : 'NO'}</span>
                        )}
                        <span style={{ color: 'var(--gold)', display: 'inline-flex', flexShrink: 0 }}>
                          <PaperplaneIcon />
                        </span>
                      </button>
                    );
                  })}
                  <button onClick={() => setSharePickerOpen(false)} style={sharePickCancel}>
                    {sharedQids.size > 0 ? 'Done' : 'Cancel'}
                  </button>
                </div>
              )}
              {copied && (
                <p style={{ margin: '8px 0 0', fontSize: 12.5, fontWeight: 700, color: 'var(--gold)' }}>
                  Link copied — paste it to a friend!
                </p>
              )}
            </div>
          )}

        </div>
      )}

      {/* The ballot — answerable while unlocked, locked picks for review once
          submitted (they sit below the celebration card above).

          Normally this is exactly the six. For a CATEGORY-CHALLENGED arrival it
          is their arena's six-question first, then that arena's back section,
          then the rest of the six (util/focusedBallot.js). The section run gets
          a heading so it never reads as "why are there nine questions today?",
          and those cards carry no 2x chip — section double-downs are
          expressive-only server-side (FP-2 D4), so offering one would be a lie. */}
      {/* THE INTERMISSION CARD. Deliberately does NOT wear the full
          post-submit celebration: that one paints "+6 coins" and "streak +1"
          optimistically, and the day's award needs all six DAILY-SIX ids
          voted — which is exactly what has not happened yet. Promising a
          reward that never arrives would be worse than the divider this
          replaces. So it celebrates what they actually did, makes the install
          the loud thing, and leaves finishing the six as a plain button. */}
      {focusIntermission && !locked && (
        <div className="ps-card ps-center" style={{ borderColor: 'var(--gold)', marginBottom: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: 23, background: 'var(--gold)', color: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '2px auto 12px', fontSize: 24, fontWeight: 900 }}>
            ✓
          </div>
          <p style={{ fontWeight: 900, fontSize: 22, margin: '0 0 6px' }}>Your calls are in</p>
          <p className="ps-muted" style={{ margin: '0 0 16px', lineHeight: 1.5 }}>
            {matchup?.inviter
              ? `Your week with ${matchup.inviter} starts now. Get the app and every day's six finds you.`
              : 'Get the app and every day’s six finds you.'}
          </p>
          {isAnonymous ? (
            <button
              onClick={() => {
                logWebEvent('web_save_cta_tapped', { source: 'focus_intermission' });
                openSaveAccount({ reason: 'install_gate' });
              }}
              style={installBtnGold}
            >
              Save your account
            </button>
          ) : !hideInstallPrompts() ? (
            onIOS ? (
              <a
                href={appStoreUrl('web_focus_intermission')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logWebEvent('web_install_cta_tapped', { platform: 'ios', source: 'focus_intermission' })}
                style={installBtnGold}
              >
                Install the Predict Six App
              </a>
            ) : (
              <button
                onClick={async () => {
                  const platform = onAndroid ? 'android' : 'desktop';
                  logWebEvent('web_install_cta_tapped', { platform, source: 'focus_intermission' });
                  if (canInstallPwa) {
                    const outcome = await promptPwaInstall();
                    logWebEvent('web_install_prompt_outcome', { platform, outcome });
                  } else {
                    setShowA2HS((v) => !v);
                  }
                }}
                style={installBtnGold}
              >
                Install the Predict Six App
              </button>
            )
          ) : null}
          {/* The rest of the day, as a button — never as three cards that
              appear under a finish line. */}
          <button
            onClick={() => {
              logWebEvent('web_focus_stage_two_opened', { category: focusCategory });
              setFocusIntermission(false);
              setFocusStageTwo(true);
              requestAnimationFrame(() => {
                cardRefs.current[focusedEntries.length]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              });
            }}
            style={intermissionSecondary}
          >
            Finish today’s six
          </button>
        </div>
      )}

      {/* Back Sections for a player who did NOT arrive on a category challenge:
          the six first, then "What are you interested in?", then that category's
          extra questions (Tom 2026-07-28). A FOCUSED arrival is excluded — their
          section is already in the ballot above, and re-offering it here would
          show the same three questions twice. */}
      {locked && sectionsVisible === true && !focusActive && sectionPrefs !== null && (
        <BackSections
          uid={uid}
          initialPreferences={sectionPrefs}
          onPreferencesSaved={setSectionPrefs}
        />
      )}

      {!focusIntermission && ballot.map((entry, i) => {
        const { q, kind, index } = entry;
        const isSectionStart = kind === 'section' && ballot[i - 1]?.kind !== 'section';
        // First of the held-back daily-six questions (beat two).
        // The "N more to finish today's six" divider is a returning-player
        // affordance. On the League first run it lands right where the user
        // believes they just finished, turning a completed six into a chore
        // (Tom, 2026-08-11) — so the ballot reads as one continuous set and
        // the submit is the finish line.
        const isDeferredStart =
          focusStageTwo && i === focusedEntries.length && !leagueFirstRun;
        return (
          <div key={q.id} ref={(el) => (cardRefs.current[i] = el)}>
            {isDeferredStart && (
              <div style={{ margin: '22px 2px 10px' }}>
                <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 800 }}>
                  {deferredSix.length} more to finish today&rsquo;s six
                </h3>
                <p
                  className="ps-muted"
                  style={{ margin: '5px 0 0', fontSize: 13, lineHeight: 1.45 }}
                >
                  Your {focusCategory} calls are saved. Complete the six to start
                  your streak and bank today&rsquo;s coins.
                </p>
              </div>
            )}
            {isSectionStart && (
              <div style={{ margin: '18px 2px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CategoryIcon category={focusCategory} size={19} />
                  <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 800 }}>
                    The {focusCategory} section
                  </h3>
                </div>
                <p
                  className="ps-muted"
                  style={{ margin: '5px 0 0', fontSize: 13, lineHeight: 1.45 }}
                >
                  These are what your {focusCategory} game is scored on this week.
                </p>
              </div>
            )}
            <QuestionCard
              question={q}
              index={i}
              disabled={locked || submitting}
              onAnswer={(ans) => handleAnswer(kind, index, ans, i)}
              onShare={locked && kind === 'six' ? () => shareQuestion(q) : undefined}
              isDoubleDown={kind === 'six' && q.isDoubleDown === true}
              onToggleDoubleDown={
                submitting || kind === 'section'
                  ? undefined
                  : () => handleToggleDoubleDown(index)
              }
              onChangeAnswer={
                locked && !submitting && kind === 'six'
                  ? () => handleChangeAnswerRequest(index)
                  : undefined
              }
              onContext={() => setContextTarget({ id: q.id })}
            />
          </div>
        );
      })}

      {/* The context modal (web parity W2) — resolved from the LIVE ballot by
          id so an answer made inside it is reflected if it's reopened. The
          answer buttons mount only while the ballot is answerable; post-submit
          the modal is read-only with the locked pick (iOS parity). */}
      {(() => {
        if (!contextTarget) return null;
        const entry = ballot.find((e) => e.q.id === contextTarget.id);
        if (!entry) return null;
        const entryIdx = ballot.indexOf(entry);
        return (
          <ContextModal
            question={entry.q}
            onAnswer={
              !locked && !submitting
                ? (ans) => handleAnswer(entry.kind, entry.index, ans, entryIdx)
                : undefined
            }
            onClose={() => setContextTarget(null)}
          />
        );
      })()}

      {/* Inline Submit (not fixed — the bottom nav owns the bottom edge).
          Hidden during the intermission: the ballot is not on screen, so a
          Submit under the celebration would have nothing to submit. */}
      {!locked && !focusIntermission && (
        <div ref={submitRef} style={{ marginTop: 4, scrollMarginBottom: 80 }}>
          {submitError && (
            <p
              role="alert"
              style={{
                margin: '0 0 8px',
                color: 'var(--danger)',
                fontWeight: 700,
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              {submitError}
            </p>
          )}
          <button
            onClick={inStageOne ? handleFocusStageOne : handleSubmit}
            disabled={!allAnswered || submitting}
            style={{
              width: '100%',
              padding: '16px 0',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: allAnswered ? 'var(--gold)' : 'var(--surface)',
              color: allAnswered ? '#1a1a1a' : 'var(--secondary-text)', // near-black on gold (A2)
              fontWeight: 800,
              fontSize: 16,
              cursor: allAnswered && !submitting ? 'pointer' : 'default',
              transition: 'background 150ms',
            }}
          >
            {submitting
              ? 'Submitting…'
              : allAnswered
                ? focusStageTwo
                  ? "Finish today's six"
                  : 'Submit my 6'
                : `${answeredCount} of ${progress.total} answered`}
          </button>
          {!allAnswered && (
            <p
              className="ps-muted ps-center"
              style={{ fontSize: 12, margin: '8px 0 0' }}
            >
              You can change any answer until you submit.
            </p>
          )}
        </div>
      )}

    </main>
  );
}

// C1 (Tom 2026-07-07) — the loss-aversion lead line prepended above the
// install pitch when the post-submit `earned.streak` is ≥2. One compact
// line, not a card; gold to match the earned strip.
function StreakLeadLine({ streak }) {
  return (
    <p
      className="ps-center"
      style={{ margin: '0 0 6px', color: 'var(--gold)', fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}
    >
      🔥 {streak}-day streak — don’t let it break.
    </p>
  );
}

// R2 (Tom 2026-07-07) — the pre-submit streak-at-risk line above the ballot,
// shown to a returning player with a streak ≥2 who hasn't submitted yet.
function StreakRiskLine({ streak }) {
  return (
    <p
      className="ps-center"
      style={{ margin: '0 0 8px', color: 'var(--gold)', fontWeight: 800, fontSize: 13.5, lineHeight: 1.4 }}
    >
      🔥 {streak}-day streak on the line — today’s six are live.
    </p>
  );
}

const retryBtn = {
  marginTop: 10,
  padding: '10px 18px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--cta-bg)',
  color: 'var(--cta-text)',
  fontWeight: 700,
};
// Orange flame for the streak tile (replaces the 🔥 emoji, which rendered
// inconsistently across platforms). Same glyph as Profile's FlameIcon.
function FlameIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#E0622A" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M12 2c.6 2.8-1.2 4.3-2.5 5.6C8 9 6.8 10.3 6.8 12.6a5.2 5.2 0 0 0 10.4.3c0-2-1-3.4-1.9-4.6-.3 1-.9 1.7-1.7 2-.2-2.6-.9-5.5-1.6-8.3z" />
    </svg>
  );
}

// Paper-plane glyph for the "Challenge a friend" share prompt.
function PaperplaneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3 11L21 3l-8 18-2.2-7.2z" />
    </svg>
  );
}

// Coin glyph for the share-bonus tally (mirrors Profile.jsx's CoinIcon; inherits
// the surrounding gold via currentColor).
function CoinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9" fill="currentColor" />
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="1.6" />
    </svg>
  );
}

const guestLinkInline = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 13,
  cursor: 'pointer',
};
// Quieter "Log in" — demoted next to the gold "Save my progress" so a new
// player reaches for Save first (audit C2).
const guestLoginInline = {
  ...guestLinkInline,
  color: 'var(--secondary-text)',
  fontWeight: 600,
};
// Primary "Create account" CTA pill under the Today title — gold-filled so it's
// the clear conversion action (the How-it-works control below drops its outline
// so this is the only pill drawing the eye).
const createAccountPill = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 24px',
  borderRadius: 999,
  border: 'none',
  background: 'var(--gold)',
  color: '#1a1a1a',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
};
// "Challenge a friend" post-submit share prompt (the viral beat).
const shareInvite = {
  margin: '0 0 18px',
  padding: '16px 16px 14px',
  borderRadius: 'var(--radius)',
  border: '1px solid rgba(212,168,75,0.4)',
  background: 'rgba(212,168,75,0.08)',
  textAlign: 'center',
};
const shareInviteTitle = {
  margin: '0 0 4px',
  fontWeight: 900,
  fontSize: 16,
  color: 'var(--primary-text)',
};
const shareInviteSub = {
  margin: '0 0 12px',
  fontSize: 13,
  lineHeight: 1.45,
};
const shareInviteBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 0',
  borderRadius: 'var(--radius-sm)',
  border: '1.5px solid var(--gold)',
  background: 'transparent',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 15,
  cursor: 'pointer',
};
// Per-question share picker rows (post-submit "send a prediction" flow).
// Gold gradient marking an already-shared question in the picker: strongest on
// the left under the question text, fading to transparent before the YES/NO
// badge (gold #c9973a = rgb(201,151,58)).
const SHARED_ROW_GRADIENT =
  'linear-gradient(90deg, rgba(201,151,58,0.34) 0%, rgba(201,151,58,0.17) 46%, rgba(201,151,58,0) 80%)';
const sharePickRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid rgba(212,168,75,0.35)',
  background: 'transparent',
  color: 'var(--primary-text)',
  cursor: 'pointer',
  textAlign: 'left',
};
const sharePickText = {
  flex: 1,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};
const sharePickPick = {
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 800,
  color: 'var(--gold)',
  border: '1px solid var(--gold)',
  borderRadius: 999,
  padding: '2px 8px',
};
const sharePickCancel = {
  marginTop: 2,
  padding: '6px 0',
  border: 'none',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
};
const saveCta = {
  width: '100%',
  padding: '13px 0',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--gold)',
  color: '#1a1a1a', // near-black on gold for AA contrast (audit A2)
  fontWeight: 800,
  fontSize: 15,
};
// Secondary, reminders-framed Save (outline so it doesn't compete with the
// gold primary above) — the anon retention hook in the locked card (audit C7).
const remindersAnonBtn = {
  width: '100%',
  marginTop: 10,
  padding: '11px 0',
  borderRadius: 'var(--radius-sm)',
  border: '1.5px solid var(--gold)',
  background: 'transparent',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
};
const installBtn = {
  display: 'block',
  width: '100%',
  textAlign: 'center',
  padding: '14px 0',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--cta-bg)',
  color: 'var(--cta-text)',
  fontWeight: 800,
  fontSize: 15,
  textDecoration: 'none',
  marginTop: 8,
};
// Gold install CTA in the locked-in card (Tom 2026-07-18, item #12) — gold
// fill, near-black text, no top margin (it leads its own block).
// The intermission's "Finish today's six" — outlined so the gold install CTA
// above it stays the loud one (Tom, 2026-08-11: the install is the point of
// this screen; the rest of the day is available, not urged).
const intermissionSecondary = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 0',
  marginTop: 10,
  borderRadius: 'var(--radius-sm)',
  border: '1.5px solid var(--gold)',
  background: 'transparent',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 15,
  cursor: 'pointer',
  textAlign: 'center',
};

const installBtnGold = {
  ...installBtn,
  background: 'var(--gold)',
  color: '#1a1a1a',
  border: 'none',
  marginTop: 0,
};
// Prominent gold "Save your account" CTA in the locked card (Tom 2026-07-20) —
// same gold as Install, uppercased so the two stacked CTAs read as a small
// hierarchy (save, then install).
const saveAccountBtn = {
  ...installBtnGold,
  cursor: 'pointer',
  marginBottom: 14,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

// Post-submit earned strip (streak · coins + 6-day bonus line).
const earnedWrap = {
  marginTop: 18,
  // Breathing room before the "Play with a friend" card below it (Tom 2026-07-20).
  marginBottom: 14,
  padding: '14px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--gold-tint, rgba(212,160,23,0.10))',
  border: '1px solid var(--gold-border, rgba(212,160,23,0.30))',
};
const earnedStats = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
};
const earnedStat = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
};
const earnedValue = {
  fontWeight: 900,
  fontSize: 18,
  lineHeight: 1.1,
  color: 'var(--text)',
  whiteSpace: 'nowrap',
};
const earnedLabel = {
  fontSize: 11,
  fontWeight: 600,
  marginTop: 2,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};
const earnedDivider = {
  width: 1,
  alignSelf: 'stretch',
  margin: '2px 0',
  background: 'var(--gold-border, rgba(212,160,23,0.30))',
};
const earnedBonus = {
  margin: '10px 0 0',
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--gold)',
  lineHeight: 1.35,
};
